import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Material
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Typography from '@mui/material/Typography';
import Box from "@mui/material/Box";
import CircularProgress from '@mui/material/CircularProgress';
import ImportExport from '@mui/icons-material/ImportExport';

// Components
import Dropdown from '../../../../../components/inputs/dropdown';
import Deposit from './deposit';
import Imprest from './imprest';
import Transfer from './transfer';
import { Authenticate } from "../../../../authentication";

// Hooks
import useUser from '../../../../../hooks/user/useUser';
import useTotals from '../../../../../hooks/cashRegisters/useTotals';
import useCashRegisters from '../../../../../hooks/cashRegisters/useCashRegisters';
import useMonthlyImprest from '../../../../../hooks/cashRegisters/useMonthlyImprest';
import useNotify from "../../../../../hooks/notify";
import useAuthorize from '../../../../../hooks/user/useAuthorize';

// Models
import { cashRegisterMovementModel, movementTypes, cashTypes, subTypes, subTypesFormat } from "../../../../../models/CashRegisterMovementEntity";
import { Permissions } from '../../../../../models/Roles';
// Services 
import CashRegisterService from '../../../../../services/cashRegisterService';
import FileService from '../../../../../services/fileService';
import Advance from './advance';

const CreateEgressModal = ({ onClose, onSave, open }) => {
    const [cashRegister, setCashRegister] = useState()
    const [loading, setLoading] = useState(false);
    const { email, pv, branch, business } = useUser();
    const { cashRegisters } = useCashRegisters();
    const { monthlyImprest } = useMonthlyImprest(branch.id);
    const { totals, getTotals } = useTotals();
    const { Notify, variants } = useNotify();
    const isAuthorized = useAuthorize();

    const initMovement = {
        ...cashRegisterMovementModel,
        type: movementTypes.egress,
        cashType: cashTypes.cash,
        cashRegisterDestination: null,
        currency: business.defaultCurrency
    };

    const [movement, setMovement] = useState(initMovement);

    useEffect(() => {
        if (cashRegister && cashRegister.id) {
            getTotals(cashRegister.id);
            setMovement(state => ({
                ...state,
                cashRegisterId: cashRegister.id,
                cashRegisterName: cashRegister.name
            }))
        }
    }, [cashRegister, getTotals]);

    useEffect(() => {
        if (email && pv) {
            setMovement(state => ({
                ...state,
                userEmail: email,
                billingCode: pv.billingCode
            }))
        }
    }, [email, pv]);

    const handleOptionClick = (option) =>
        setMovement({ ...movement, subType: option })

    const handleCancel = () => {
        if (movement.subType)
            setMovement(initMovement)
        else
            onClose();
    }

    const handleCashRegisterChange = (event) =>
        setCashRegister(event.target.value)

    const handleSave = async () => {
        if (movement.subType === subTypes.Transfer) {
            await createTransferMovement(); 
            return;
        }

        await createMovement();
    }

    const createTransferMovement = async () => {
        try {
            setLoading(true);
            const response = await Authenticate();
            if(!response.account)
                throw new Error("Error al intentar realizar el logueo");
            
            const req = {
                ...movement,
                branchId: branch.id,
                amount: Number(movement.amount),
                cashRegisterId: movement.cashRegisterDestination.id,
                userEmail: response.account.username
            }
            await CashRegisterService.createTransferMovement(cashRegister.id, req);
            onSave();
        } catch (err) {
            Notify(err ? err.toString() : "Error al intentar realizar transferencia entre cajas", variants.ERROR);
        }   
        setLoading(false);
    }

    const createMovement = async () => {
        try {
            setLoading(true);
            const req = {
                ...movement,
                cashRegisterId: cashRegister.id,
                branchId: branch.id,
                amount: Number(movement.amount),
                externalPrefix: Number(movement.externalPrefix) || null,
                externaNumber: Number(movement.externaNumber) || null
            }
            if (req.files.length > 0) {
                const fileUrl = await FileService.Upload(req.files[0]);
                delete req.files;
                req.fileUrl = fileUrl;
            }

            await CashRegisterService.CreateMovement(req);

            onSave();
        } catch (err) {
            console.error(err)
            Notify("Error al intentar realizar el movimiento: " + err?.message, variants.ERROR);
        }
        setLoading(false);
    }

    const GetCashTotal = () =>
        totals?.[`Cash${movement.currency}`]?.total || 0;

    const disableSave = () => {
        const cashTotal = GetCashTotal();

        if (movement.subType === subTypes.Deposit)
            return cashTotal - movement.amount < 0 ||
                movement.amount <= 0 || !movement?.externaNumber || 
                movement?.externalPrefix <= 0 || !movement?.externaDate ||
                movement?.bankAccountId <= 0;

        if (movement.subType === subTypes.Imprest)
            return ( 
                !movement.reasonId  ||
                movement.amount <= 0 ||
                movement.files.length === 0 ||
                (monthlyImprest.fixedAmount - movement.amount) < 0 ||
                (cashTotal - movement.amount) < 0
            );

        if (movement.subType === subTypes.Transfer && movement.cashRegisterDestination?.isImprest)
            return (monthlyImprest.totalAmountImprest + Number(movement.amount)) > monthlyImprest.fixedAmount ||
                (cashTotal - movement.amount) <= 0 ||
                movement.amount <= 0 || !movement.reasonId ||
                !movement.cashRegisterDestination;

        if (movement.subType === subTypes.Transfer && !movement.cashRegisterDestination?.isImprest)
            return (cashTotal - movement.amount) < 0 ||
                movement.amount <= 0 || !movement.reasonId ||
                !movement.cashRegisterDestination;

        if (movement.subType === subTypes.Adjustment)
            return cashTotal - movement.amount < 0 || movement.amount <= 0;        

        return false;
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
            maxWidth='sm'
        >
            <DialogTitle id="alert-dialog-title">
                Egreso de Caja
                <Grid container spacing={1}>
                    {
                        cashRegister && cashRegister.name &&
                        <Grid size={6}>
                            <Box display="flex" justifyContent='flex-start'>
                                <Typography color="primary" variant="body1" gutterBottom>
                                    Caja seleccionada:&nbsp;
                                </Typography>
                                <Typography variant="body1">
                                    <strong>
                                        {cashRegister.name}
                                    </strong>
                                </Typography>
                            </Box>
                        </Grid>
                    }
                    {
                        movement && movement.subType &&
                        <Grid size={6}>
                            <Box display="flex" justifyContent='flex-end'>
                                <Typography color="primary" variant="body1" gutterBottom>
                                    Tipo de movimiento:&nbsp;
                                </Typography>
                                <Typography variant="body1">
                                    <strong>
                                        {subTypesFormat[movement.subType]}
                                    </strong>
                                </Typography>
                            </Box>
                        </Grid>
                    }
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                {!movement.subType &&
                    <Grid container spacing={3} justifyContent='center'>
                        <Grid container spacing={1} size={12}>
                            <Grid size={12}>
                                <Typography variant='inherit' color='primary'> Seleccionar caja: </Typography>
                            </Grid>
                            <Grid size={12}>
                                <Dropdown
                                    fullWidth
                                    name='cashRegister'
                                    label='Caja'
                                    size='small'
                                    onChange={handleCashRegisterChange}
                                    getOptionLabel={(option) => `${option?.name}`}
                                    getOptionValue={(option) => option}
                                    values={cashRegisters}
                                    value={cashRegister || ''}
                                />
                            </Grid>
                        </Grid>
                        <Grid>
                            <Button
                                variant='outlined'
                                disabled={!cashRegister || cashRegister?.isImprest}
                                style={{ textTransform: 'none' }}
                                onClick={() => handleOptionClick(subTypes.Deposit)}
                            >
                                <Grid container direction='column' alignItems='center'>
                                    <AccountBalanceIcon />
                                    <Typography variant='body2'>
                                        Depósitos
                                    </Typography>
                                </Grid>
                            </Button>
                        </Grid>
                        <Grid>
                            <Button
                                variant='outlined'
                                disabled={!monthlyImprest || !cashRegister}
                                style={{ textTransform: 'none' }}
                                onClick={() => handleOptionClick(subTypes.Transfer)}
                            >
                                <Grid container direction='column' alignItems='center'>
                                    <ImportExport />
                                    <Typography variant='body2'>
                                        Transferencia
                                    </Typography>
                                </Grid>
                            </Button>
                        </Grid>
                        <Grid>
                            <Button
                                variant='outlined'
                                disabled={!cashRegister || !cashRegister?.isImprest}
                                style={{ textTransform: 'none' }}
                                onClick={() => handleOptionClick(subTypes.Imprest)}
                            >
                                <Grid container direction='column' alignItems='center'>
                                    <AccountBalanceWalletIcon />
                                    <Typography variant='body2'>
                                        Fondo fijo
                                    </Typography>
                                </Grid>
                            </Button>
                        </Grid>
                        <Grid>
                            { 
                                isAuthorized([Permissions.AjusteCaja]) &&
                            <Button
                                variant='outlined'
                                disabled={!cashRegister || cashRegister?.isImprest}
                                style={{ textTransform: 'none' }}
                                onClick={() => handleOptionClick(subTypes.Adjustment)}
                            >
                                <Grid container direction='column' alignItems='center'>
                                    <AttachMoneyIcon />
                                    <Typography variant='body2'>
                                        Ajuste de Caja
                                    </Typography>
                                </Grid>
                            </Button>
                            }
                        </Grid>
                    </Grid>
                }
                {
                    movement.subType === subTypes.Deposit &&
                    <Deposit
                        movement={movement}
                        setMovement={setMovement}
                        totals={totals}
                    />
                }
                {
                    movement.subType === subTypes.Imprest &&
                    <Imprest
                        movement={movement}
                        setMovement={setMovement}
                        totals={totals}
                        fixedAmount={monthlyImprest?.fixedAmount || 0}
                    />
                }
                {
                    movement.subType === subTypes.Transfer &&
                    <Transfer
                        movement={movement}
                        setMovement={setMovement}
                        totals={totals}
                        totalAmountImprest={
                            monthlyImprest?.movementTotals?.find(
                                e => e.movementSubTypes === "Imprest")?.total || 0
                        }
                        fixedAmount={monthlyImprest?.fixedAmount || 0}
                        cashRegisters={cashRegisters?.filter(c => c.id !== cashRegister?.id)}
                    />
                }
                {
                    movement.subType === subTypes.Adjustment &&
                    <Advance
                        movement={movement}
                        setMovement={setMovement}
                        totals={totals}
                        fixedAmount={monthlyImprest?.fixedAmount || 0}
                    />
                }
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleCancel}
                    color="primary"
                    disabled={loading}
                >
                    Cancelar
                </Button>
                {
                    movement.subType &&
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="outlined"
                        autoFocus
                        disabled={disableSave() || loading}
                    >
                        {loading 
                            ? <Box display="flex" flexDirection='row' alignItems='center' gap={1}><CircularProgress size={18} /> ...Guardando</Box> 
                            : 'Guardar'
                        }
                    </Button>
                }
            </DialogActions>
        </Dialog>
    );
}

CreateEgressModal.defaultProps = {
    onSave: () => { },
    onClose: () => { },
}

CreateEgressModal.prototype = {
    onSave: PropTypes.func,
    onClose: PropTypes.func,
    open: PropTypes.bool,
    cashRegister: PropTypes.object
}

export default CreateEgressModal;